import { alertController } from "@ionic/core";

// App-wide confirm dialog for delete/clear actions — the same Ionic alert as
// the notifications clear/delete confirm (red Cancel / green Confirm via the
// global ion-alert CSS). Presented imperatively through alertController
// rather than a permanently mounted <IonAlert isOpen>, because a controlled
// overlay's internal "presented" flag desyncs under busy parent re-renders
// (the admin layout polls every 30s) and then silently stops opening.
// Resolves true when confirmed, false when cancelled/dismissed.
export function confirmAlert({ header, message = "", confirmText = "Confirm", cancelText = "Cancel", cssClass }) {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (v) => { if (!settled) { settled = true; resolve(v); } };
    alertController
      .create({
        header,
        message,
        cssClass,
        buttons: [
          { text: cancelText, role: "cancel" },
          { text: confirmText, handler: () => settle(true) },
        ],
      })
      .then((alert) => {
        alert.onDidDismiss().then(() => settle(false));
        alert.present();
      })
      // Never leave an action stuck if the overlay stack is wedged
      .catch(() => settle(window.confirm(message ? `${header}\n\n${message}` : header)));
  });
}

// Text-input prompt, same reliable imperative pattern. Resolves the trimmed
// string on confirm, or null when cancelled/dismissed/empty.
export function promptAlert({ header, message = "", placeholder = "", confirmText = "Create", cancelText = "Cancel", value = "", allowEmpty = false, cssClass }) {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (v) => { if (!settled) { settled = true; resolve(v); } };
    alertController
      .create({
        header,
        message,
        cssClass,
        inputs: [{ name: "value", type: "text", placeholder, value }],
        buttons: [
          { text: cancelText, role: "cancel" },
          // With allowEmpty, a blank save comes back as "" (cancel stays null)
          { text: confirmText, handler: (data) => settle(allowEmpty ? String(data?.value || "").trim() : (String(data?.value || "").trim() || null)) },
        ],
      })
      .then((alert) => {
        alert.onDidDismiss().then(() => settle(null));
        alert.present();
      })
      .catch(() => settle(String(window.prompt(message ? `${header}\n\n${message}` : header) || "").trim() || null));
  });
}

// Informational alert (single OK button), same reliable imperative pattern.
export function infoAlert({ header, message = "", cssClass, okText = "OK" }) {
  return alertController
    .create({ header, message, cssClass, buttons: [okText] })
    .then((alert) => alert.present())
    .catch(() => window.alert(message ? `${header}\n\n${message}` : header));
}
